import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');
  const [imageError, setImageError] = useState(false);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-4">该文章可能已被移除或链接无效</p>
            <Link
              to="/"
              className="whitespace-nowrap cursor-pointer px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors inline-block"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article.id);
  const catName = article.category === 'agricultural' ? '农业喷水灌溉' : article.category === 'landscape' ? '景观喷水装置' : '工业喷水冷却';

  return (
    <>
      <SEOMeta
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.penshuixitong.cn/article/${article.id}`}
        ogType="article"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.publishDate,
            image: article.coverImage,
            publisher: {
              '@type': 'Organization',
              name: '喷水系统专业解决方案',
            },
          }),
        }}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 pt-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '设备分类', href: '/category' },
                { label: catName, href: `/category?tab=${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-foreground-500">
              <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">
                {catName}
              </span>
              <span>{article.author}</span>
              <span>{article.publishDate}</span>
              <span>{article.readTime}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>
          </header>

          <div className="mb-8 rounded-lg overflow-hidden h-[300px] md:h-[420px]">
            {!imageError ? (
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 喷水系统`}
                className="w-full h-full object-cover object-top"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full bg-background-100 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                    <i className="ri-image-line text-2xl text-foreground-400"></i>
                  </div>
                  <span className="text-sm text-foreground-500">{article.title}</span>
                </div>
              </div>
            )}
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800
              prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
              prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
              prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3
              prose-p:leading-relaxed prose-p:mb-4
              prose-li:leading-relaxed
              prose-strong:text-foreground-900
              prose-ol:my-4 prose-ul:my-4"
            dangerouslySetInnerHTML={{ __html: article.contentHtml }}
          />

          <div className="mt-8 pt-6 border-t border-background-200/70">
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag, i) => (
                <span key={i} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-12 px-4 md:px-6" aria-labelledby="related-title">
            <div className="max-w-4xl mx-auto">
              <h2 id="related-title" className="font-heading text-xl font-semibold text-foreground-900 mb-6">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="h-40 overflow-hidden">
                      <img
                        src={rel.coverImage}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {rel.title}
                      </h3>
                      <span className="text-xs text-foreground-500">{rel.readTime}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}